import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, HostListener, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, ViewEncapsulation } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router, Params, RouterModule } from "@angular/router";
import { CdkDragDrop, moveItemInArray, DragDropModule } from "@angular/cdk/drag-drop";
import { IWorkflow, IWorkflowStage, IWorkflowSecurity } from "../../../interfaces/iworkflow";
import { WorkflowConfigurationService } from "../../../services/workflowconfiguration.service";
import { ToastrService } from "ngx-toastr";
import { ICanComponentDeactivate } from "../../../interfaces/icancomponentdeactivate";
import { Observable } from "rxjs";
import { ConfirmationModalComponent } from "../../confirmationmodal/confirmationmodal.component";
import { UserSearchModalComponent } from "../../usersearchmodal/usersearchmodal.component";


@Component({
    selector: "workflowconfiguration",
    templateUrl: "./workflowconfiguration.component.html",
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, ConfirmationModalComponent, UserSearchModalComponent, DragDropModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
    styles: [`
        /* Workflow drag-drop styles - scoped to workflowconfiguration to prevent conflicts */
        workflowconfiguration .draggable-item {
            cursor: pointer;
            transition: transform 0.2s ease;
        }
        
        workflowconfiguration .d-flex[cdkDragHandle] {
            cursor: pointer;
        }
        
        workflowconfiguration .drag-handle {
            cursor: pointer;
            padding: 4px;
            color: #000000;
            font-size: 16px;
            font-weight: 800;
        }
        
        workflowconfiguration .drag-handle:hover {
            color: #000000;
        }
        
        workflowconfiguration .drag-handle:active {
            cursor: pointer;
        }
        
        workflowconfiguration .drag-handle i {
            font-weight: 400;
            transform: scaleX(0.9);
            color: #000000;
        }
        
        workflowconfiguration .draggable.cdk-drop-list-dragging .draggable-item:not(.cdk-drag-placeholder) {
            transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
        }
        
        workflowconfiguration .cdk-drop-list {
            display: block;
            min-height: 60px;
        }
        
        workflowconfiguration .draggable-item .card {
            border: 1px solid #dee2e6;
            transition: box-shadow 0.2s ease;
        }
        
        workflowconfiguration .draggable-item:hover .card {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        
        workflowconfiguration .draggable-item:hover .drag-handle {
            color: #000000 !important;
        }
        
        workflowconfiguration .draggable-item-disabled {
            opacity: 0.6;
        }
        
        workflowconfiguration .draggable-item-disabled .card {
            background-color: #f8f9fa;
        }
        
        /* CDK Drag-Drop placeholder and preview - scoped with attribute selector */
        workflowconfiguration .cdk-drag-placeholder {
            opacity: 0.4;
            background: #f8f9fa;
            border: 2px dashed #dee2e6;
        }
        
        workflowconfiguration .cdk-drag-animating {
            transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
        }
    `]
})
export class WorkflowConfigurationComponent implements OnInit, OnDestroy, AfterViewInit, ICanComponentDeactivate {

    workflow: IWorkflow;
    selectedStep: IWorkflowStage = <IWorkflowStage>{ id: null, sortOrder: 0, name: "", security: [] };
    hasDefaultWorkflowConfiguration: boolean;
    defaultWorkflow: IWorkflow;
    countryName: string;
    originalData: string;

    @ViewChild("confirmModal") confirmModal;

    constructor(private router: Router,
        private activatedRoute: ActivatedRoute,
        private workflowService: WorkflowConfigurationService,
        private toastsMgr: ToastrService) {
    }

    ngOnInit(): void {
        let countryCode: string;
        const configuration: IWorkflow = this.activatedRoute.snapshot.data["workflowconfiguration"] as IWorkflow;
        this.defaultWorkflow = this.activatedRoute.snapshot.data["defaultWorkflowConfiguration"] as IWorkflow;
        this.hasDefaultWorkflowConfiguration = this.defaultWorkflow != null && this.defaultWorkflow.workflowStages.length > 0;
        this.activatedRoute.params.subscribe((params: Params) => {
            countryCode = params["countrycode"];
            this.countryName = params["countryname"];
        });
        this.workflow = configuration;
        if (!configuration.countryCode) {
            this.workflow.countryCode = countryCode;
        }
    }

    ngAfterViewInit(): void {
        this.originalData = JSON.stringify(this.workflow);
    }


    ngOnDestroy(): void {
        // CDK Drag and Drop cleanup is handled automatically
    }

    /**
     * is called when "Add Step" is clicked
     * Will call create for new workflos or update for existing workflows
     */
    addStep(): void {
        const stepNumber: number = this.workflow.workflowStages.length + 1;
        const name: string = "Step " + stepNumber;
        this.workflow.workflowStages.push(<IWorkflowStage>{ id: null, sortOrder: stepNumber, name: name, security: [] });
    }

    /**
     * Handles the drag and drop functionality using Angular CDK
     */
    drop(event: CdkDragDrop<IWorkflowStage[]>): void {
        if (event.previousIndex !== event.currentIndex) {
            moveItemInArray(this.workflow.workflowStages, event.previousIndex, event.currentIndex);
            
            // Update sort order after reordering
            this.updateSortOrder();
        }
    }

    /**
     * Updates the sort order of workflow stages after drag and drop
     */
    private updateSortOrder(): void {
        let sortOrder = 1;
        this.workflow.workflowStages.forEach((stage: IWorkflowStage) => {
            stage.sortOrder = sortOrder;
            sortOrder++;
        });
    }

    /**
     * TrackBy function for ngFor optimization
     */
    trackByStepId(index: number, item: IWorkflowStage): any {
        return item.id || index;
    }

    /**
     * is called when the user saves the entire workflow
     * Will call create for new workflos or update for existing workflows
     */
    saveWorkflow(): void {
        // Ensure sort order is up to date
        this.updateSortOrder();

        if (!this.workflow.id) {
            // create
            this.workflowService.createWorkflow(this.workflow).subscribe(i => {
                this.workflow = i;
                this.originalData = JSON.stringify(this.workflow);
                this.toastsMgr.success("Workflow successfully created.");
            });
        } else {
            // update
            this.workflowService.updateWorkflow(this.workflow).subscribe(i => {
                this.workflow = i;
                this.originalData = JSON.stringify(this.workflow);
                this.toastsMgr.success("Workflow successfully updated.");
            });
        }
    }

    /**
     * is called when "Delete" is clicked
     * Sets the selected workflow step to enabled
     */
    stepDeleted(step: IWorkflowStage): void {
        step.disabled = true;
        if (!step.id) {
            this.workflow.workflowStages.splice(this.workflow.workflowStages.indexOf(step), 1);
        }
    }

    /**
     * is called when "Undo/Enable" is clicked
     * Sets the selected workflow step to enabled
     */
    stepEnabled(step: IWorkflowStage): void {
        step.disabled = false;
    }

    /**
     * is called when "Add Users" is clicked
     * Sets the selected workflow step accordingly
     */
    stepSelected(step: IWorkflowStage): void {
        this.selectedStep = step;
    }

    removeUser(step: IWorkflowStage, security: IWorkflowSecurity): void {
         step.security.splice(step.security.indexOf(security), 1);
    }

    /**
     * is called when the apply default workflow button is clicked
     * copies stages from default into the selected country
     */
    applyDefaultWorkflow(): void {
        this.defaultWorkflow.workflowStages.filter(e => !e.disabled).forEach(r => {
            const copy: IWorkflowStage = JSON.parse(JSON.stringify(r));
            copy.id = null;
            this.workflow.workflowStages.push(copy);
        });
    }


    @HostListener("window:beforeunload")
    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        if (this.originalData !== JSON.stringify(this.workflow)) {
            this.confirmModal.openModal();
            return false;
        } else {
            return true;
        }
    }
    onAccept(): void {
        // reset the the original back to whats currenlty in itemConfigSet, this allows to pass check  in candeactivate
        this.originalData = JSON.stringify(this.workflow);
        this.router.navigate([this.navigatingTo]);
        this.confirmModal.closeModal();
    }

    onDecline(): void {
        this.confirmModal.closeModal();
    }

    navigatingTo: string;
}


